import { SelectItem } from 'primeng/api';

import {
  AmortizationType,
  amortizationTypeLabel,
  amortizationTypeOptions,
  armFixedTermLabel,
  armFixedTermOptions,
  automatedUWSystemLabel,
  automatedUWSystemOptions,
  buyDownLabel,
  buyDownOptions,
  citizenshipLabel,
  citizenshipOptions,
  desiredLockPeriodLabel,
  desiredLockPeriodOptions,
  fhaCaseAssignedOptions,
  loanPurposeOptions,
  loanTermLabel,
  loanTermOptions,
  MortgageType,
  mortgageTypeLabel,
  noOfUnitsLabel,
  noOfUnitsOptions,
  occupancyTypeLabel,
  occupancyTypeOptions,
  ScenarioRow,
  propertyTypeLabel,
  propertyTypeOptions,
  secondMortgageLabel,
  secondMortgageOptions,
  usdaCaseAssignedOptions,
  vaFundingFeeTypeLabel,
  vaFundingFeeTypeOptions,
  yesNoOptions,
  stateOptions,
  loanPurposeLabel,
  mortgageTypeOptionsScenarios,
  LoanPurpose,
  productTypeLabel,
  productTypeOptionsScenarios,
  fhaCaseAssignedLabel,
  usdaCaseAssignedLabel,
  prepaymentPenaltyLabel,
  prepaymentPenaltyOptions,
  MIProvidersMapFiltered,
  miProvidersOptionsFiltered,
  pmiTypeMap,
  pmiTypeOptions,
  armSubsequentChangePeriodMap,
  armSubsequentChangePeriodOptions,
  PmiType,
  sectionOfActLabel,
  sectionOfActOptions,
} from '.';

export type ScenarioFieldType = 'string' | 'number' | 'currency' | 'percent' | 'boolean' | 'date' | 'enum';

export type PrimeNGFilterType = 'text' | 'numeric' | 'boolean' | 'date' | 'select';

export const filterType: Record<ScenarioFieldType, PrimeNGFilterType> = {
  ['string']: 'text',
  ['number']: 'numeric',
  ['currency']: 'numeric',
  ['percent']: 'numeric',
  ['boolean']: 'boolean',
  ['date']: 'date',
  ['enum']: 'select',
};

export interface ScenarioColumn {
  label: string;
  type: ScenarioFieldType;
  /** Options passed to pipe specified by format field */
  digitsInfo?: string;
  /** Suffix to append to text values */
  suffix?: string;
  /** Used with `'format': 'map'` to specify value mapping */
  map?: Record<string, string>;
  /** Used with `'format': 'map'` to define filter options */
  options?: SelectItem[];
  editable?: true;
  groupable?: true;
  /** Whether the value should be underlined */
  underline?: true;

  // sortByField is only necessary for columns like "Purchase Price" and "Estimated Value"... where
  // these grid columns bind to the same underlying database column ("purchasePriceOrEstimatedValue").
  // When left as undefined, the column will be sorted by the "keyof PipelineScenario" that this
  // ScenarioColumn is tied to.
  sortByField?: string;

  // For cases where the value to display in a column cell is conditional.
  // Example - "PMI / MIP / FF / G Fee [$]" - needs to show either Conv or Govt value.
  conditionalValueBinding?: (PipelineScenario) => any;
}

export const columnInfo: Partial<Record<keyof ScenarioRow, ScenarioColumn>> = {
  ['secondMortgage']: {
    label: '2nd Mortgage',
    type: 'enum',
    map: secondMortgageLabel,
    options: secondMortgageOptions,
    conditionalValueBinding: (rowData: ScenarioRow) => {
      return rowData.clear1ScenarioPricingsId ? null : rowData.secondMortgage;
    },
  },
  ['amortizationType']: {
    label: 'Amortization Type',
    type: 'enum',
    map: amortizationTypeLabel,
    options: amortizationTypeOptions,
    conditionalValueBinding: (rowData: ScenarioRow) => {
      return rowData.clear1ScenarioPricingsId ? null : rowData.amortizationType;
    },
  },
  ['borrowerFirstName']: {
    label: 'Applicant First Name',
    type: 'string',
  },
  ['borrowerLastName']: {
    label: 'Applicant Last Name',
    type: 'string',
  },
  ['scenarioArmFixedTerm']: {
    label: 'ARM Fixed Term',
    type: 'enum',
    map: armFixedTermLabel,
    options: armFixedTermOptions,
    conditionalValueBinding: (rowData: ScenarioRow) => {
      if (rowData.clear1ScenarioPricingsId) {
        return null;
      }
      return Number(rowData.amortizationType) === AmortizationType.ARM ? rowData.scenarioArmFixedTerm : null;
    },
  },
  ['automatedUnderwritingSystem']: {
    label: 'Automated U/W System',
    type: 'enum',
    map: automatedUWSystemLabel,
    options: automatedUWSystemOptions,
    conditionalValueBinding: (rowData: ScenarioRow) => {
      return rowData.clear1ScenarioPricingsId ? null : rowData.automatedUnderwritingSystem;
    },
  },
  ['baseLoanAmount']: {
    label: 'Base Loan Amount',
    type: 'currency',
    digitsInfo: '1.0-0',
  },
  ['baseLtv']: {
    label: 'Base LTV',
    type: 'percent',
    digitsInfo: '1.2-2',
    conditionalValueBinding: (rowData: ScenarioRow) => {
      return rowData.clear1ScenarioPricingsId ? null : rowData.baseLtv;
    },
  },
  ['armFirstCap']: {
    label: 'ARM First Rate Adjustment Cap',
    type: 'percent',
    digitsInfo: '1.0-0',
  },
  ['armPeriodicCap']: {
    label: 'ARM Subsequent Rate Adjustment Cap',
    type: 'percent',
    digitsInfo: '1.0-0',
  },
  ['armRelativeCap']: {
    label: 'ARM Life Rate Adjustment Cap',
    type: 'percent',
    digitsInfo: '1.0-0',
  },
  ['htiWithoutMi']: {
    label: 'HTI without MI',
    type: 'percent',
    digitsInfo: '1.2-2',
  },
  ['dtiWithoutMi']: {
    label: 'DTI without MI',
    type: 'percent',
    digitsInfo: '1.2-2',
  },
  ['borrowerPaysMi']: {
    label: 'Borrower Pays MI',
    type: 'boolean',
    options: yesNoOptions,
    conditionalValueBinding: (rowData: ScenarioRow) => {
      return [MortgageType.Conforming, MortgageType.NonConforming].includes(+rowData.scenarioMortgageType)
        ? rowData.borrowerPaysMiConv
        : rowData.borrowerPaysMiGovt;
    },
  },
  ['buyDown']: {
    label: 'Buydown',
    type: 'enum',
    map: buyDownLabel,
    options: buyDownOptions,
  },
  ['cashOutAmount']: {
    label: 'Cash Out',
    type: 'currency',
    digitsInfo: '1.0-0',
  },
  ['city']: {
    label: 'City',
    type: 'string',
  },
  ['citizenship']: {
    label: 'Citizenship',
    type: 'enum',
    map: citizenshipLabel,
    options: citizenshipOptions,
  },
  ['communityAffordableSecond']: {
    label: 'Community/Affordable 2nd',
    type: 'boolean',
    options: yesNoOptions,
  },
  ['county']: {
    label: 'County',
    type: 'string',
  },
  ['discountRebateAmount']: {
    label: 'Discount/Rebate $',
    type: 'currency',
    digitsInfo: '1.2-2',
    conditionalValueBinding: (rowData: ScenarioRow) => {
      return rowData.clear1ScenarioPricingsId ? null : rowData.discountRebateAmount;
    },
  },
  ['discountRebatePercent']: {
    label: 'Discount/Rebate %',
    type: 'percent',
    digitsInfo: '1.3-3',
    conditionalValueBinding: (rowData: ScenarioRow) => {
      return rowData.clear1ScenarioPricingsId ? null : rowData.discountRebatePercent;
    },
  },
  ['downPaymentAmount']: {
    label: 'Down Payment $',
    type: 'currency',
    digitsInfo: '1.0-0',
    conditionalValueBinding: (rowData: ScenarioRow) => {
      if (rowData.clear1ScenarioPricingsId) {
        return null;
      }
      return +rowData.loanPurpose === LoanPurpose.Purchase ? rowData.downPaymentAmount : null;
    },
  },
  ['downPaymentPercent']: {
    label: 'Down Payment %',
    type: 'percent',
    digitsInfo: '1.3-3',
    conditionalValueBinding: (rowData: ScenarioRow) => {
      if (rowData.clear1ScenarioPricingsId) {
        return null;
      }
      return +rowData.loanPurpose === LoanPurpose.Purchase ? rowData.downPaymentPercent : null;
    },
  },
  ['dti']: {
    label: 'DTI',
    type: 'percent',
    digitsInfo: '1.2-2',
  },
  ['exemptFromFundingFee']: {
    label: 'Exempt Funding Fee',
    type: 'boolean',
    options: yesNoOptions,
  },
  ['fhaCaseAssigned']: {
    label: 'FHA Case # Assigned',
    type: 'enum',
    map: fhaCaseAssignedLabel,
    options: fhaCaseAssignedOptions,
  },
  ['fico']: {
    label: 'FICO',
    type: 'number',
    digitsInfo: '1.0-0',
  },
  ['borrowerFico']: {
    label: 'Borrower FICO',
    type: 'number',
    digitsInfo: '1.0-0',
  },
  ['borrowerSelfEmployed']: {
    label: 'Borrower Self-Employed',
    type: 'boolean',
    options: yesNoOptions,
  },
  ['borrowerHadForeclosure']: {
    label: 'Borrower Foreclosure (7 Yrs)',
    type: 'boolean',
    options: yesNoOptions,
  },
  ['borrowerHadBankruptcy']: {
    label: 'Borrower Bankruptcy (7 Yrs)',
    type: 'boolean',
    options: yesNoOptions,
  },
  ['coBorrowerFico']: {
    label: 'Co-Borrower FICO',
    type: 'number',
    digitsInfo: '1.0-0',
  },
  ['coBorrowerSelfEmployed']: {
    label: 'Co-Borrower Self-Employed',
    type: 'boolean',
    options: yesNoOptions,
  },
  ['coBorrowerHadForeclosure']: {
    label: 'Co-Borrower Foreclosure (7 Yrs)',
    type: 'boolean',
    options: yesNoOptions,
  },
  ['coBorrowerHadBankruptcy']: {
    label: 'Co-Borrower Bankruptcy (7 Yrs)',
    type: 'boolean',
    options: yesNoOptions,
  },
  ['bestXMi']: {
    label: 'BestX MI',
    type: 'boolean',
    options: yesNoOptions,
  },
  ['pmiType']: {
    label: 'PMI Type',
    type: 'enum',
    map: pmiTypeMap,
    options: pmiTypeOptions,
  },
  ['armSubsequentChangePeriod']: {
    label: 'ARM Subsequent Change Period',
    type: 'enum',
    map: armSubsequentChangePeriodMap,
    options: armSubsequentChangePeriodOptions,
  },
  ['miProvider']: {
    label: 'Vendor',
    type: 'enum',
    map: MIProvidersMapFiltered,
    options: miProvidersOptionsFiltered,
  },
  ['monthlyPremiumAmount']: {
    label: 'Monthly Premium',
    type: 'currency',
    digitsInfo: '1.2-2',
    conditionalValueBinding: (rowData: ScenarioRow) => {
      return [PmiType.BorrowerMonthlyNonRefundableConstant, PmiType.SplitConstantUpfront100].includes(rowData.pmiType)
        ? rowData.monthlyPremiumAmount
        : null;
    },
  },
  ['monthlyPremiumRate']: {
    label: 'Monthly Premium Rate',
    type: 'percent',
    digitsInfo: '1.3-3',
    conditionalValueBinding: (rowData: ScenarioRow) => {
      return [PmiType.BorrowerMonthlyNonRefundableConstant, PmiType.SplitConstantUpfront100].includes(rowData.pmiType)
        ? rowData.monthlyPremiumRate
        : null;
    },
  },
  ['coveragePercent']: {
    label: 'Coverage Percent',
    type: 'percent',
    digitsInfo: '1.0-0',
  },
  ['isFinanceEntireAmount']: {
    label: 'Financed',
    type: 'boolean',
    options: yesNoOptions,
    conditionalValueBinding: (rowData: ScenarioRow) => {
      return [MortgageType.Conforming, MortgageType.NonConforming].includes(+rowData.scenarioMortgageType)
        ? rowData.isFinanceEntireAmountConv
        : rowData.isFinanceEntireAmountGovt;
    },
  },
  ['firstTimeHomeBuyer']: {
    label: 'First Time Home Buyer',
    type: 'boolean',
    options: yesNoOptions,
  },
  ['heLoanAmount']: {
    label: 'HE Loan Amount',
    type: 'currency',
    digitsInfo: '1.0-0',
  },
  ['helocDrawnAmount']: {
    label: 'HELOC Drawn Amount',
    type: 'currency',
    digitsInfo: '1.0-0',
  },
  ['helocLineAmount']: {
    label: 'HELOC Line Amount',
    type: 'currency',
    digitsInfo: '1.0-0',
  },
  ['interestOnly']: {
    label: 'Interest Only',
    type: 'boolean',
    options: yesNoOptions,
  },
  ['isPud']: {
    label: 'PUD',
    type: 'boolean',
    options: yesNoOptions,
  },
  ['interestRate']: {
    label: 'Interest Rate',
    type: 'percent',
    digitsInfo: '1.3-3',
    underline: true,
    groupable: true,
    conditionalValueBinding: (rowData: ScenarioRow) => {
      return rowData.clear1ScenarioPricingsId ? null : rowData.interestRate;
    },
  },
  ['lastPriced']: {
    label: 'Last Priced',
    type: 'date',
    groupable: true,
    conditionalValueBinding: (rowData: ScenarioRow) => {
      return rowData.clear1ScenarioPricingsId ? null : rowData.lastPriced;
    },
  },
  ['loanPurpose']: {
    label: 'Loan Purpose',
    type: 'enum',
    map: loanPurposeLabel,
    options: loanPurposeOptions,
    groupable: true,
  },
  ['lockExpirationDate']: {
    label: 'Lock Expiration Date',
    type: 'date',
    groupable: true,
    conditionalValueBinding: (rowData: ScenarioRow) => {
      return rowData.clear1ScenarioPricingsId ? null : rowData.lockExpirationDate;
    },
  },
  ['scenarioLoanTerm']: {
    label: 'Loan Term',
    type: 'enum',
    map: loanTermLabel,
    options: loanTermOptions,
    conditionalValueBinding: (rowData: ScenarioRow) => {
      return rowData.clear1ScenarioPricingsId ? null : rowData.scenarioLoanTerm;
    },
  },
  ['lockPeriod']: {
    label: 'Lock Period',
    type: 'enum',
    map: desiredLockPeriodLabel,
    options: desiredLockPeriodOptions,
    conditionalValueBinding: (rowData: ScenarioRow) => {
      if (rowData.clear1ScenarioPricingsId) {
        return null;
      }
      return rowData.lockPeriod === 0 ? '0 Days' : rowData.lockPeriod;
    },
  },
  ['monthsOfReserve']: {
    label: 'Mo. Reserves',
    type: 'number',
    digitsInfo: '1.0-0',
  },
  ['monthlyPI']: {
    label: 'Monthly P&I',
    type: 'currency',
    digitsInfo: '1.2-2',
    conditionalValueBinding: (rowData: ScenarioRow) => {
      return rowData.clear1ScenarioPricingsId ? null : rowData.monthlyPI;
    },
  },
  ['monthlyPITI']: {
    label: 'Monthly PITI',
    type: 'currency',
    digitsInfo: '1.2-2',
    underline: true,
  },
  ['scenarioMortgageType']: {
    label: 'Mortgage Type',
    type: 'enum',
    map: mortgageTypeLabel,
    options: mortgageTypeOptionsScenarios,
    groupable: true,
    conditionalValueBinding: (rowData: ScenarioRow) => {
      return rowData.clear1ScenarioPricingsId ? null : rowData.scenarioMortgageType;
    },
  },
  ['noOfStories']: {
    label: 'No. of Stories',
    type: 'number',
    digitsInfo: '1.0-0',
  },
  ['noOfUnits']: {
    label: 'No. of Units',
    type: 'enum',
    map: noOfUnitsLabel,
    options: noOfUnitsOptions,
  },
  ['noOfFinancedProperties']: {
    label: 'No. of Financed Properties',
    type: 'number',
    digitsInfo: '1.0-0',
  },
  ['nonOccupantCoBorrower']: {
    label: 'Non Occupant Additional Borrower',
    type: 'boolean',
    options: yesNoOptions,
  },
  ['mhAdvantage']: {
    label: 'MH Advantage',
    type: 'boolean',
    options: yesNoOptions,
  },
  ['occupancyType']: {
    label: 'Occupancy Type',
    type: 'enum',
    map: occupancyTypeLabel,
    options: occupancyTypeOptions,
  },
  ['upfrontPmiMipFfGFeePercent']: {
    label: 'PMI / MIP / FF / G Fee [%] Upfront',
    type: 'percent',
    digitsInfo: '1.3-3',
    conditionalValueBinding: (rowData: ScenarioRow) => {
      return [MortgageType.Conforming, MortgageType.NonConforming].includes(+rowData.scenarioMortgageType)
        ? rowData.upfrontPmiMipFfGFeePercentConv
        : rowData.upfrontPmiMipFfGFeePercentGovt;
    },
  },
  ['upfrontPmiMipFfGFeeAmount']: {
    label: 'PMI / MIP / FF / G Fee [$] Upfront',
    type: 'currency',
    digitsInfo: '1.2-2',
    conditionalValueBinding: (rowData: ScenarioRow) => {
      return [MortgageType.Conforming, MortgageType.NonConforming].includes(+rowData.scenarioMortgageType)
        ? rowData.upfrontPmiMipFfGFeeAmountConv
        : rowData.upfrontPmiMipFfGFeeAmountGovt;
    },
  },
  ['upfrontPmiMipFfGFeeAmountFinanced']: {
    label: 'PMI / MIP / FF / G Fee [$] Financed',
    type: 'currency',
    digitsInfo: '1.2-2',
    conditionalValueBinding: (rowData: ScenarioRow) => {
      return [MortgageType.Conforming, MortgageType.NonConforming].includes(+rowData.scenarioMortgageType)
        ? rowData.upfrontPmiMipFfGFeeAmountFinancedConv
        : rowData.upfrontPmiMipFfGFeeAmountFinancedGovt;
    },
  },
  ['upfrontPmiMipFfGFeeAmountCash']: {
    label: 'PMI / MIP / FF / G Fee [$] Paid in Cash',
    type: 'currency',
    digitsInfo: '1.2-2',
    conditionalValueBinding: (rowData: ScenarioRow) => {
      return [MortgageType.Conforming, MortgageType.NonConforming].includes(+rowData.scenarioMortgageType)
        ? rowData.upfrontPmiMipFfGFeeAmountCashConv
        : rowData.upfrontPmiMipFfGFeeAmountCashGovt;
    },
  },
  ['monthlyPmiMipFfGFeePercent']: {
    label: 'PMI / MIP / FF / G Fee [%] Monthly',
    type: 'percent',
    digitsInfo: '1.3-3',
    conditionalValueBinding: (rowData: ScenarioRow) => {
      return [MortgageType.Conforming, MortgageType.NonConforming].includes(+rowData.scenarioMortgageType)
        ? rowData.monthlyPmiMipFfGFeePercent
        : rowData.monthlyPremiumRate;
    },
  },
  ['monthlyPmiMipFfGFeeAmount']: {
    label: 'PMI / MIP / FF / G Fee [$] Monthly',
    type: 'currency',
    digitsInfo: '1.2-2',
    conditionalValueBinding: (rowData: ScenarioRow) => {
      return [MortgageType.Conforming, MortgageType.NonConforming].includes(+rowData.scenarioMortgageType)
        ? rowData.monthlyPmiMipFfGFeeAmount
        : rowData.monthlyPremiumAmount;
    },
  },
  ['upfrontPremiumRate']: {
    label: 'Upfront Premium Rate',
    type: 'percent',
    digitsInfo: '1.3-3',
    conditionalValueBinding: (rowData: ScenarioRow) => {
      return [PmiType.BorrowerSingleNonRefundable, PmiType.SplitConstantUpfront100].includes(rowData.pmiType) ? rowData.upfrontPremiumRate : null;
    },
  },
  ['upfrontPremiumAmount']: {
    label: 'Upfront Premium',
    type: 'currency',
    digitsInfo: '1.2-2',
    conditionalValueBinding: (rowData: ScenarioRow) => {
      return [PmiType.BorrowerSingleNonRefundable, PmiType.SplitConstantUpfront100].includes(rowData.pmiType) ? rowData.upfrontPremiumAmount : null;
    },
  },
  ['price']: {
    label: 'Price',
    type: 'number',
    digitsInfo: '1.3-3',
    conditionalValueBinding: (rowData: ScenarioRow) => {
      return rowData.clear1ScenarioPricingsId ? null : rowData.price;
    },
  },
  ['productName']: {
    label: 'Product Name',
    type: 'string',
    groupable: true,
    conditionalValueBinding: (rowData: ScenarioRow) => {
      return rowData.clear1ScenarioPricingsId ? null : rowData.productName;
    },
  },
  ['propertyType']: {
    label: 'Property Type',
    type: 'enum',
    map: propertyTypeLabel,
    options: propertyTypeOptions,
  },
  ['purchasePrice']: {
    label: 'Purchase Price',
    type: 'currency',
    digitsInfo: '1.0-0',
    sortByField: 'purchasePrice',
  },
  ['estimatedValue']: {
    label: 'Estimated Value',
    type: 'currency',
    digitsInfo: '1.0-0',
    sortByField: 'estimatedValue',
  },
  ['scenarioName']: {
    label: 'Scenario Name',
    type: 'string',
    editable: true,
  },
  ['selfEmployed']: {
    label: 'Self Employed',
    type: 'boolean',
    options: yesNoOptions,
  },

  ['state']: {
    label: 'State',
    type: 'enum',
    options: stateOptions,
    groupable: true,
  },
  ['street']: {
    label: 'Street Address',
    type: 'string',
  },
  ['totalLoanAmount']: {
    label: 'Total Loan Amount',
    type: 'currency',
    digitsInfo: '1.2-2',
    conditionalValueBinding: (rowData: ScenarioRow) => {
      return [MortgageType.Conforming, MortgageType.NonConforming].includes(+rowData.scenarioMortgageType)
        ? rowData.totalLoanAmountConv
        : rowData.totalLoanAmountGovt;
    },
  },
  ['usdaCaseAssigned']: {
    label: 'USDA Case # Assigned',
    type: 'enum',
    map: usdaCaseAssignedLabel,
    options: usdaCaseAssignedOptions,
    conditionalValueBinding: (rowData: ScenarioRow) => {
      return rowData.clear1ScenarioPricingsId ? null : rowData.usdaCaseAssigned;
    },
  },
  ['vaFundingFeeType']: {
    label: 'VA Funding Fee Type',
    type: 'enum',
    map: vaFundingFeeTypeLabel,
    options: vaFundingFeeTypeOptions,
  },
  ['waiveEscrows']: {
    label: 'Waive Escrows',
    type: 'boolean',
    options: yesNoOptions,
  },
  ['zip']: {
    label: 'Zip Code',
    type: 'string',
    conditionalValueBinding: (rowData: ScenarioRow) => {
      let zip = rowData.zip?.toString();
      if (zip.length === 9) {
        zip = `${zip.slice(0, 5)}-${zip.slice(5)}`;
      }

      return zip;
    },
  },
  ['monthlyQualifyingIncome']: {
    label: 'Monthly Qualifying Income',
    type: 'currency',
    digitsInfo: '1.2-2',
    conditionalValueBinding: (rowData: ScenarioRow) => {
      return rowData.clear1ScenarioPricingsId ? null : rowData.monthlyQualifyingIncome;
    },
  },
  ['prepaymentPenalty']: {
    label: 'Prepayment Penalty',
    type: 'enum',
    map: prepaymentPenaltyLabel,
    options: prepaymentPenaltyOptions,
  },
  ['productType']: {
    label: 'Product Type',
    type: 'enum',
    map: productTypeLabel,
    options: productTypeOptionsScenarios,
  },
  ['expandedGuidelines']: {
    label: 'Expanded Guidelines',
    type: 'boolean',
    options: yesNoOptions,
  },
  ['estimatedClosingDate']: {
    label: 'Estimated Closing Date',
    type: 'date',
    groupable: true,
    conditionalValueBinding: (rowData: ScenarioRow) => {
      return rowData.clear1ScenarioPricingsId ? null : rowData.estimatedClosingDate;
    },
  },
  ['fhaPrevEndorsementDate']: {
    label: 'Prev. Endorsement Date',
    type: 'date',
    groupable: true,
    conditionalValueBinding: (rowData: ScenarioRow) => {
      return rowData.clear1ScenarioPricingsId ? null : rowData.fhaPrevEndorsementDate;
    },
  },
  ['fhaSectionOfAct']: {
    label: 'Section of Act',
    type: 'enum',
    map: sectionOfActLabel,
    options: sectionOfActOptions.filter((option) => option.value !== null),
  },
  ['cardsAndOtherDebtPaidOff']: {
    label: 'Credit Cards and Other Debts Paid Off',
    type: 'currency',
    digitsInfo: '1.2-2',
    conditionalValueBinding: (rowData: ScenarioRow) => {
      return rowData.clear1ScenarioPricingsId ? null : rowData.cardsAndOtherDebtPaidOff;
    },
  },
  ['earnestMoneyDeposit']: {
    label: 'Earnest Money Deposit',
    type: 'currency',
    digitsInfo: '1.2-2',
    conditionalValueBinding: (rowData: ScenarioRow) => {
      return rowData.clear1ScenarioPricingsId ? null : rowData.earnestMoneyDeposit;
    },
  },
  ['mortgageLoansToBePaid']: {
    label: 'Balance of Mortgage Loans to be Paid',
    type: 'currency',
    digitsInfo: '1.2-2',
    conditionalValueBinding: (rowData: ScenarioRow) => {
      return rowData.clear1ScenarioPricingsId ? null : rowData.mortgageLoansToBePaid;
    },
  },
  ['lumpSumSellerCredit']: {
    label: 'Lump Sum Seller Credit',
    type: 'currency',
    digitsInfo: '1.2-2',
    conditionalValueBinding: (rowData: ScenarioRow) => {
      return rowData.clear1ScenarioPricingsId ? null : rowData.lumpSumSellerCredit;
    },
  },
};
